import React, { useState } from 'react';
import { Stack, Text, Group, Box, Card, SegmentedControl, ScrollArea, Modal, TextInput, Button, FileInput, NumberInput, Grid } from '@mantine/core';
import { Wallet, TrendingUp, AlertTriangle, Upload, X, CreditCard, Lock, CheckCircle } from 'lucide-react';
import { PaymentItem } from '../components/PaymentItem';
import type { Payment } from '../types';
// Mock data
const mockPayments: Payment[] = [{
  id: '1',
  propertyId: '1',
  description: 'Booking Amount',
  amount: 50000,
  dueDate: '2023-05-20',
  status: 'paid',
  paidDate: '2023-05-18'
}, {
  id: '2',
  propertyId: '1',
  description: '1st Installment - 10%',
  amount: 125000,
  dueDate: '2023-08-15',
  status: 'paid',
  paidDate: '2023-08-14'
}, {
  id: '3',
  propertyId: '1',
  description: '2nd Installment - 10%',
  amount: 125000,
  dueDate: '2023-11-15',
  status: 'paid',
  paidDate: '2023-11-12'
}, {
  id: '4',
  propertyId: '1',
  description: '3rd Installment - 10%',
  amount: 125000,
  dueDate: '2024-02-15',
  status: 'upcoming'
}, {
  id: '5',
  propertyId: '1',
  description: '4th Installment - 10%',
  amount: 125000,
  dueDate: '2024-01-10',
  status: 'overdue'
}, {
  id: '6',
  propertyId: '1',
  description: '5th Installment - 10%',
  amount: 125000,
  dueDate: '2024-05-15',
  status: 'upcoming'
}];
export function FinancialLedger() {
  const [filter, setFilter] = useState<'all' | 'paid' | 'upcoming' | 'overdue'>('all');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    cardHolder: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: ''
  });
  const filteredPayments = mockPayments.filter(payment => {
    if (filter === 'all') return true;
    return payment.status === filter;
  });
  const totalPaid = mockPayments.filter(p => p.status === 'paid').reduce((acc, p) => acc + p.amount, 0);
  const totalPending = mockPayments.filter(p => p.status !== 'paid').reduce((acc, p) => acc + p.amount, 0);
  const overdueCount = mockPayments.filter(p => p.status === 'overdue').length;
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0
    }).format(amount);
  };
  const handleUploadProof = (payment: Payment) => {
    setSelectedPayment(payment);
    setUploadModalOpen(true);
  };
  const handlePayNow = (payment: Payment) => {
    setSelectedPayment(payment);
    setPaymentSuccess(false);
    setPaymentModalOpen(true);
  };
  const handlePaymentSubmit = () => {
    // Simulate payment processing
    setPaymentSuccess(true);
    setTimeout(() => {
      setPaymentModalOpen(false);
      setPaymentSuccess(false);
      setCardDetails({
        cardNumber: '',
        cardHolder: '',
        expiryMonth: '',
        expiryYear: '',
        cvv: ''
      });
    }, 2000);
  };
  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\s/g, '');
    const chunks = cleaned.match(/.{1,4}/g);
    return chunks ? chunks.join(' ') : cleaned;
  };
  return <>
      <ScrollArea h="100%" offsetScrollbars data-id="element-270">
        <Stack gap="lg" pb={100} data-id="element-271">
          {/* Header */}
          <Box data-id="element-272">
            <Text fw={700} size="xl" c="#1a2332" mb={4} data-id="element-273">
              Payment Ledger
            </Text>
            <Text size="sm" c="#64748b" data-id="element-274">
              Track and manage your installments
            </Text>
          </Box>

          {/* Summary Cards */}
          <Group grow gap="sm" data-id="element-275">
            <Card p="md" radius="md" style={{
            backgroundColor: '#d1fae5',
            border: '1px solid #a7f3d0'
          }} data-id="element-276">
              <Group gap="sm" data-id="element-277">
                <Wallet size={20} color="#10b981" data-id="element-278" />
                <Stack gap={2} data-id="element-279">
                  <Text size="xs" c="#065f46" fw={500} data-id="element-280">
                    Total Paid
                  </Text>
                  <Text fw={700} size="md" c="#065f46" data-id="element-281">
                    {formatCurrency(totalPaid)}
                  </Text>
                </Stack>
              </Group>
            </Card>
            <Card p="md" radius="md" style={{
            backgroundColor: '#f1f5f9',
            border: '1px solid #e2e8f0'
          }} data-id="element-282">
              <Group gap="sm" data-id="element-283">
                <TrendingUp size={20} color="#64748b" data-id="element-284" />
                <Stack gap={2} data-id="element-285">
                  <Text size="xs" c="#475569" fw={500} data-id="element-286">
                    Pending
                  </Text>
                  <Text fw={700} size="md" c="#1a2332" data-id="element-287">
                    {formatCurrency(totalPending)}
                  </Text>
                </Stack>
              </Group>
            </Card>
          </Group>

          {/* Overdue Alert */}
          {overdueCount > 0 && <Card p="md" radius="md" style={{
          backgroundColor: '#fee2e2',
          border: '1px solid #fecaca'
        }} data-id="element-288">
              <Group gap="sm" data-id="element-289">
                <AlertTriangle size={20} color="#ef4444" data-id="element-290" />
                <Stack gap={2} data-id="element-291">
                  <Text fw={600} size="sm" c="#991b1b" data-id="element-292">
                    {overdueCount} Overdue Payment{overdueCount > 1 ? 's' : ''}
                  </Text>
                  <Text size="xs" c="#b91c1c" data-id="element-293">
                    Please pay now or upload payment proof to avoid penalties
                  </Text>
                </Stack>
              </Group>
            </Card>}

          {/* Filter Tabs */}
          <SegmentedControl value={filter} onChange={value => setFilter(value as typeof filter)} data={[{
          label: 'All',
          value: 'all'
        }, {
          label: 'Paid',
          value: 'paid'
        }, {
          label: 'Upcoming',
          value: 'upcoming'
        }, {
          label: 'Overdue',
          value: 'overdue'
        }]} fullWidth radius="md" size="sm" styles={{
          root: {
            backgroundColor: '#f1f5f9'
          },
          indicator: {
            backgroundColor: '#1a2332'
          },
          label: {
            fontWeight: 500,
            fontSize: 13,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(26, 35, 50, 0.05)'
            },
            '&[data-active]': {
              color: 'white',
              '&:hover': {
                backgroundColor: 'transparent'
              }
            }
          }
        }} data-id="element-294" />

          {/* Payment List */}
          <Stack gap="sm" data-id="element-295">
            {filteredPayments.length === 0 ? <Card p="xl" radius="md" withBorder data-id="element-296">
                <Text ta="center" c="#64748b" data-id="element-297">
                  No payments found
                </Text>
              </Card> : filteredPayments.map(payment => <PaymentItem key={payment.id} payment={payment} onUploadProof={() => handleUploadProof(payment)} onPayNow={() => handlePayNow(payment)} data-id="element-298" />)}
          </Stack>
        </Stack>
      </ScrollArea>

      {/* Upload Proof Modal */}
      <Modal opened={uploadModalOpen} onClose={() => setUploadModalOpen(false)} title={<Text fw={700} size="lg" c="#1a2332" data-id="element-300">
            Upload Payment Proof
          </Text>} radius="md" centered data-id="element-299">
        <Stack gap="md" data-id="element-301">
          {selectedPayment && <Card p="sm" radius="md" style={{
          backgroundColor: '#f8fafc'
        }} data-id="element-302">
              <Text size="sm" c="#64748b" data-id="element-303">
                {selectedPayment.description}
              </Text>
              <Text fw={700} c="#1a2332" data-id="element-304">
                {formatCurrency(selectedPayment.amount)}
              </Text>
            </Card>}

          <TextInput label="Transaction Reference" placeholder="Enter bank reference number" radius="md" data-id="element-305" />

          <FileInput label="Upload Receipt" placeholder="Select file" accept="image/*,application/pdf" radius="md" leftSection={<Upload size={16} data-id="element-307" />} data-id="element-306" />

          <Group justify="flex-end" gap="sm" mt="md" data-id="element-308">
            <Button variant="subtle" color="gray" radius="md" onClick={() => setUploadModalOpen(false)} data-id="element-309">
              Cancel
            </Button>
            <Button color="green" radius="md" leftSection={<Upload size={16} data-id="element-311" />} styles={{
            root: {
              backgroundColor: '#10b981',
              '&:hover': {
                backgroundColor: '#059669'
              }
            }
          }} data-id="element-310">
              Submit Proof
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Pay with Visa Modal */}
      <Modal opened={paymentModalOpen} onClose={() => setPaymentModalOpen(false)} title={!paymentSuccess && <Group gap="sm" data-id="element-313">
              <CreditCard size={20} color="#1a2332" data-id="element-314" />
              <Text fw={700} size="lg" c="#1a2332" data-id="element-315">
                Pay with Visa
              </Text>
            </Group>} radius="md" centered size="md" data-id="element-312">
        {paymentSuccess ? <Stack align="center" gap="md" py="xl" data-id="element-316">
            <Box p="lg" style={{
          backgroundColor: '#d1fae5',
          borderRadius: '50%'
        }} data-id="element-317">
              <CheckCircle size={48} color="#10b981" data-id="element-318" />
            </Box>
            <Text fw={700} size="lg" c="#1a2332" ta="center" data-id="element-319">
              Payment Successful!
            </Text>
            <Text size="sm" c="#64748b" ta="center" data-id="element-320">
              Your payment has been processed successfully.
            </Text>
          </Stack> : <Stack gap="md" data-id="element-321">
            {selectedPayment && <Card p="md" radius="md" style={{
          background: 'linear-gradient(135deg, #1a2332 0%, #2d3a4f 100%)'
        }} data-id="element-322">
                <Stack gap={4} data-id="element-323">
                  <Text size="xs" c="rgba(255,255,255,0.7)" data-id="element-324">
                    Amount to Pay
                  </Text>
                  <Text fw={700} size="xl" c="white" data-id="element-325">
                    {formatCurrency(selectedPayment.amount)}
                  </Text>
                  <Text size="xs" c="rgba(255,255,255,0.7)" data-id="element-326">
                    {selectedPayment.description}
                  </Text>
                </Stack>
              </Card>}

            <TextInput label="Card Number" placeholder="1234 5678 9012 3456" radius="md" leftSection={<CreditCard size={16} data-id="element-328" />} value={cardDetails.cardNumber} onChange={e => setCardDetails({
          ...cardDetails,
          cardNumber: formatCardNumber(e.target.value.replace(/\D/g, '').slice(0, 16))
        })} maxLength={19} data-id="element-327" />

            <TextInput label="Cardholder Name" placeholder="AHMED AL-RASHID" radius="md" value={cardDetails.cardHolder} onChange={e => setCardDetails({
          ...cardDetails,
          cardHolder: e.target.value.toUpperCase()
        })} data-id="element-329" />

            <Grid gutter="sm" data-id="element-330">
              <Grid.Col span={4} data-id="element-331">
                <TextInput label="Expiry Month" placeholder="MM" radius="md" value={cardDetails.expiryMonth} onChange={e => setCardDetails({
              ...cardDetails,
              expiryMonth: e.target.value.replace(/\D/g, '').slice(0, 2)
            })} maxLength={2} data-id="element-332" />
              </Grid.Col>
              <Grid.Col span={4} data-id="element-333">
                <TextInput label="Expiry Year" placeholder="YY" radius="md" value={cardDetails.expiryYear} onChange={e => setCardDetails({
              ...cardDetails,
              expiryYear: e.target.value.replace(/\D/g, '').slice(0, 2)
            })} maxLength={2} data-id="element-334" />
              </Grid.Col>
              <Grid.Col span={4} data-id="element-335">
                <TextInput label="CVV" placeholder="123" radius="md" leftSection={<Lock size={14} data-id="element-337" />} value={cardDetails.cvv} onChange={e => setCardDetails({
              ...cardDetails,
              cvv: e.target.value.replace(/\D/g, '').slice(0, 3)
            })} maxLength={3} type="password" data-id="element-336" />
              </Grid.Col>
            </Grid>

            <Box p="sm" style={{
          backgroundColor: '#f1f5f9',
          borderRadius: 8,
          border: '1px solid #e2e8f0'
        }} data-id="element-338">
              <Group gap="xs" data-id="element-339">
                <Lock size={14} color="#64748b" data-id="element-340" />
                <Text size="xs" c="#64748b" data-id="element-341">
                  Your payment is secured with 256-bit SSL encryption
                </Text>
              </Group>
            </Box>

            <Group justify="flex-end" gap="sm" mt="md" data-id="element-342">
              <Button variant="subtle" color="gray" radius="md" onClick={() => setPaymentModalOpen(false)} data-id="element-343">
                Cancel
              </Button>
              <Button color="green" radius="md" leftSection={<CreditCard size={16} data-id="element-345" />} onClick={handlePaymentSubmit} disabled={!cardDetails.cardNumber || !cardDetails.cardHolder || !cardDetails.expiryMonth || !cardDetails.expiryYear || !cardDetails.cvv} styles={{
            root: {
              backgroundColor: '#10b981',
              '&:hover': {
                backgroundColor: '#059669'
              },
              '&:disabled': {
                backgroundColor: '#d1d5db'
              }
            }
          }} data-id="element-344">
                Pay {selectedPayment && formatCurrency(selectedPayment.amount)}
              </Button>
            </Group>
          </Stack>}
      </Modal>
    </>;
}